import { Box, ButtonBase, Chip, Icon, Typography } from "@mui/material";

import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineOppositeContent,
  TimelineSeparator,
} from "@mui/lab";

import {
  IconArchive,
  IconArrowsJoin,
  IconArrowsSplit,
  IconBookmark,
  IconBusinessplan,
  IconCoin,
  IconFileSearch,
  IconLayersSelectedBottom,
  IconLayersUnion,
  IconReplace,
} from "@tabler/icons-react";
import { map, orderBy } from "lodash";
import { Moment } from "moment";

import { EventoCarteira } from "../../../../../models/eventoCarteira";
import { NotaBMF, NotaBovespa } from "../../../../../models/nota";
import { TimelineMensalProps } from "./calendarioMes";
import { SituacaoNota, TipoEvento } from "../../../../../models/enum";
import MoedaText from "../../../../util/ui/inputs/moedaText";
import InteiroText from "../../../../util/ui/inputs/inteiroText";
import Ticker from "../../../../util/ui/ticker";
import ChipCorretora from "../../../../util/ui/chipCorretora";
import ChipNegociacao from "../../../../util/ui/chipNegociacao";
import { Link } from "react-router-dom";
import { radius } from "../../../../../theme";

export function TimelineMensal({ notas, eventos }: TimelineMensalProps) {
  const timeline: EventoTimeline[] = orderBy(
    map(
      notas,
      (nota) =>
        ({
          dia: nota.dataPregao,
          tipo: TipoEventoTimeline.Nota,
          nota,
        } as EventoTimeline)
    ).concat(
      map(
        eventos,
        (evento) =>
          ({
            dia: evento.dataEvento,
            tipo: TipoEventoTimeline.Evento,
            evento,
          } as EventoTimeline)
      )
    ),
    "dia",
    "desc"
  );
  return (
    <Timeline>
      {map(timeline, (itemTimeline, index) => {
        let action = undefined;
        let icone = () => <></>;
        let conteudo = () => <></>;
        let cor:
          | "primary"
          | "secondary"
          | "success"
          | "info"
          | "error"
          | "warning"
          | undefined = "primary";
        switch (itemTimeline.tipo) {
          case TipoEventoTimeline.Nota:
            const nota = itemTimeline.nota!;
            icone = () => <IconArchive size={16} />;
            cor = "secondary";
            conteudo = () => <NotaItem nota={nota} />;
            action = (
              <ButtonBase
                component={Link}
                to={`/arquivo/nota/${nota.id}`}
                sx={{ borderRadius: `${radius.pill}px`, p: 0.5 }}
              >
                <Icon
                  sx={(theme) => ({
                    display: "flex",
                    alignItems: "center",
                    color: theme.palette.text.secondary,
                    "&:hover": { color: theme.palette.accentInk },
                  })}
                >
                  <IconFileSearch size={16} />
                </Icon>
              </ButtonBase>
            );
            break;
          case TipoEventoTimeline.Evento:
            const evento = itemTimeline.evento!;
            switch (evento.tipo) {
              case TipoEvento.DIVIDENDO:
              case TipoEvento.JUROS:
              case TipoEvento.RENDIMENTO:
                icone = () => <IconBusinessplan size={16} />;
                cor = "success";
                conteudo = () => <PagamentoItem evento={evento} />;
                break;
              case TipoEvento.SUBSCRICAO_DISTRIBUICAO:
                icone = () => <IconBookmark size={16} />;
                cor = "info";
                conteudo = () => (
                  <DistribuicaoDeSubscricaoItem evento={evento} />
                );
                break;
              case TipoEvento.DESDOBRAMENTO:
                if (evento.acao !== "CRÉDITO") return <></>;
                icone = () => <IconArrowsSplit size={16} />;
                cor = "info";
                conteudo = () => <DesdobramentoItem evento={evento} />;
                break;
              case TipoEvento.GRUPAMENTO:
                if (evento.acao !== "CRÉDITO") return <></>;
                icone = () => <IconArrowsJoin size={16} />;
                cor = "info";
                conteudo = () => <GrupamentoItem evento={evento} />;
                break;
              case TipoEvento.ATUALIZACAO:
                if (evento.acao !== "CRÉDITO") return <></>;
                icone = () => <IconReplace size={16} />;
                cor = "info";
                conteudo = () => <AtualizacaoItem evento={evento} />;
                break;
              case TipoEvento.INCORPORACAO:
                if (evento.acao !== "CRÉDITO") return <></>;
                icone = () => <IconLayersUnion size={16} />;
                cor = "info";
                conteudo = () => <IncorporacaoItem evento={evento} />;
                break;
              case TipoEvento.BONIFICACAO:
                if (evento.acao !== "CRÉDITO") return <></>;
                icone = () => <IconBusinessplan size={16} />;
                cor = "success";
                conteudo = () => <BonificacaoItem evento={evento} />;
                break;
              case TipoEvento.DISSOLUCAO:
                if (evento.acao !== "CRÉDITO") return <></>;
                icone = () => <IconLayersSelectedBottom size={16} />;
                cor = "secondary";
                conteudo = () => <DissolucaoItem evento={evento} />;
                break;
              case TipoEvento.LIQUIDACAO:
                if (evento.acao !== "CRÉDITO") return <></>;
                icone = () => <IconLayersSelectedBottom size={16} />;
                cor = "secondary";
                conteudo = () => <LiquidacaoItem evento={evento} />;
                break;
              case TipoEvento.AMORTIZACAO:
                icone = () => <IconCoin size={16} />;
                cor = "info";
                conteudo = () => <AmortizacaoItem evento={evento} />;
                break;
            }
        }
        return (
          <TimelineItem key={index}>
            <TimelineOppositeContent
              sx={{ flexGrow: 0.1, alignSelf: "center" }}
            >
              {/* Data do evento em mono tabular. */}
              <Typography
                variant="dataMono"
                color="text.secondary"
                sx={{ fontSize: 12 }}
              >
                {itemTimeline.dia.format("DD/MM")}
              </Typography>
            </TimelineOppositeContent>
            <TimelineSeparator>
              <TimelineConnector />
              {/* Dot em outline: o tipo do evento é traço, não bolha de cor. */}
              <TimelineDot color={cor} variant="outlined" sx={{ boxShadow: "none" }}>
                {icone()}
              </TimelineDot>
              <TimelineConnector />
            </TimelineSeparator>
            <TimelineContent sx={{ alignSelf: "center" }}>
              {/* Card de evento: superfície + hairline, com um filete semântico
                  à esquerda. Nada de bloco de cor saturada. */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 1.5,
                  py: 1,
                  bgcolor: "background.paper",
                  border: "1px solid",
                  borderColor: "divider",
                  borderLeftWidth: 3,
                  borderLeftStyle: "solid",
                  borderLeftColor: `${cor}.main`,
                  borderRadius: `${radius.lg}px`,
                }}
              >
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>{conteudo()}</Box>
                {action}
              </Box>
            </TimelineContent>
          </TimelineItem>
        );
      })}
    </Timeline>
  );
}

function NotaItem({ nota }: { nota: NotaBMF | NotaBovespa }) {
  return (
    <Typography component="span" variant="body2" color="text.primary">
      {`Você fez ${nota.quantidadeOperacoes} ${
        nota.quantidadeOperacoes > 1 ? "operações" : "operação"
      } `}
      <ChipNegociacao negociacao={nota.tipoNegociacao} />
      {` na `}
      <ChipCorretora codigoCorretora={nota.corretora} />
      {` `}
      {nota.situacao === SituacaoNota.Inconsistente && (
        <Chip
          size="small"
          label="Inconsistente"
          sx={(theme) => ({
            bgcolor: theme.palette.statusChip.destructive.bg,
            color: theme.palette.statusChip.destructive.fg,
          })}
        />
      )}
    </Typography>
  );
}

function PagamentoItem({ evento }: { evento: EventoCarteira }) {
  return (
    <Typography component="span" variant="body2" color="text.primary">
      {"Você recebeu "}
      <Typography component="span" variant="dataMono" sx={{ fontWeight: 600 }}>
        <MoedaText value={evento.valor! * evento.posicaoEx.quantidade} />
      </Typography>
      {` como ${(() => {
        switch (evento.tipo) {
          case TipoEvento.DIVIDENDO:
            return "Dividendos";
          case TipoEvento.JUROS:
            return "JSCP";
          case TipoEvento.RENDIMENTO:
            return "Rendimentos";

          default:
            return "Pagamento";
        }
      })()} de `}
      <Ticker size="small" codigoAtivo={evento.codigoAtivo} />
    </Typography>
  );
}

function AmortizacaoItem({ evento }: { evento: EventoCarteira }) {
  return (
    <Typography component="span" variant="body2" color="text.primary">
      {"Você recebeu "}
      <Typography component="span" variant="dataMono" sx={{ fontWeight: 600 }}>
        <MoedaText value={evento.valor! * evento.posicaoEx.quantidade} />
      </Typography>
      {" em Amortização (devolução de capital) de "}
      <Ticker size="small" codigoAtivo={evento.codigoAtivo} />
    </Typography>
  );
}

function BonificacaoItem({ evento }: { evento: EventoCarteira }) {
  return (
    <Typography component="span" variant="body2" color="text.primary">
      {"Você recebeu "}
      <Typography component="span" variant="dataMono" sx={{ fontWeight: 600 }}>
        <InteiroText
          value={Math.floor(evento.fator! * evento.posicaoEx.quantidade)}
        />
      </Typography>
      {" unidades como Bonificação de "}
      <Ticker size="small" codigoAtivo={evento.codigoAtivo} />
    </Typography>
  );
}

function DistribuicaoDeSubscricaoItem({ evento }: { evento: EventoCarteira }) {
  return (
    <Typography component="span" variant="body2" color="text.primary">
      {"Você recebeu "}
      <Typography component="span" variant="dataMono" sx={{ fontWeight: 600 }}>
        <InteiroText
          value={Math.floor(evento.fator! * evento.posicaoEx.quantidade)}
        />
      </Typography>
      {" Direitos de Subscrição de "}
      <Ticker size="small" codigoAtivo={evento.codigoAtivo} />
    </Typography>
  );
}

function DesdobramentoItem({ evento }: { evento: EventoCarteira }) {
  return (
    <Typography component="span" variant="body2" color="text.primary">
      {"Houve um "}
      <Typography component="span" variant="body2" sx={{ fontWeight: 600 }}>
        Desdobramento
      </Typography>
      {" de "}
      <Ticker size="small" codigoAtivo={evento.codigoAtivo} />
      {" de "}
      <Typography component="span" variant="dataMono" sx={{ fontWeight: 600 }}>
        {`1 para ${evento.fator! + 1}`}
      </Typography>
    </Typography>
  );
}

function GrupamentoItem({ evento }: { evento: EventoCarteira }) {
  return (
    <Typography component="span" variant="body2" color="text.primary">
      {"Houve um "}
      <Typography component="span" variant="body2" sx={{ fontWeight: 600 }}>
        Grupamento
      </Typography>
      {" de "}
      <Ticker size="small" codigoAtivo={evento.codigoAtivo} />
      {" de "}
      <Typography component="span" variant="dataMono" sx={{ fontWeight: 600 }}>
        {`${1 / evento.fator!} para 1`}
      </Typography>
    </Typography>
  );
}

function AtualizacaoItem({ evento }: { evento: EventoCarteira }) {
  return (
    <Typography component="span" variant="body2" color="text.primary">
      <Ticker size="small" codigoAtivo={evento.codigoAtivo} />
      {" sofreu uma "}
      <Typography component="span" variant="body2" sx={{ fontWeight: 600 }}>
        Atualização
      </Typography>
      {" e se tornou "}
      <Ticker size="small" codigoAtivo={evento.codigoObjeto!} />
    </Typography>
  );
}

function IncorporacaoItem({ evento }: { evento: EventoCarteira }) {
  return (
    <Typography component="span" variant="body2" color="text.primary">
      <Ticker size="small" codigoAtivo={evento.codigoAtivo} />
      {" será "}
      <Typography component="span" variant="body2" sx={{ fontWeight: 600 }}>
        Incorporado
      </Typography>
      {" por "}
      <Ticker size="small" codigoAtivo={evento.codigoObjeto!} />
    </Typography>
  );
}

function DissolucaoItem({ evento }: { evento: EventoCarteira }) {
  return (
    <Typography component="span" variant="body2" color="text.primary">
      <Ticker size="small" codigoAtivo={evento.codigoAtivo} />
      {" foi dissolvido e se tornou "}
      <Ticker size="small" codigoAtivo={evento.codigoObjeto!} />
    </Typography>
  );
}

function LiquidacaoItem({ evento }: { evento: EventoCarteira }) {
  const valorEmDinheiro = (evento.valor ?? 0) * evento.posicaoEx.quantidade;
  return (
    <Typography component="span" variant="body2" color="text.primary">
      <Ticker size="small" codigoAtivo={evento.codigoAtivo} />
      {" foi liquidado e se tornou "}
      <Ticker size="small" codigoAtivo={evento.codigoObjeto!} />
      {valorEmDinheiro > 0 && (
        <>
          {", com "}
          <Typography component="span" variant="dataMono" sx={{ fontWeight: 600 }}>
            <MoedaText value={valorEmDinheiro} />
          </Typography>
          {" também em dinheiro"}
        </>
      )}
    </Typography>
  );
}

type EventoTimeline = {
  dia: Moment;
  tipo: TipoEventoTimeline;
  nota?: NotaBMF | NotaBovespa;
  evento?: EventoCarteira;
};
enum TipoEventoTimeline {
  Nota,
  Evento,
}
